import { isDefined } from "@clipboard-health/util-ts";
import { patch } from "@src/appV2/api";
import { useCbhQueryClient } from "@src/appV2/api/queryClient";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

import {
  externalAccountsEntityType,
  type PayoutSettingsAttributes,
  payoutSettingsEntityType,
} from "./schema";
import { GET_LAST_PAYOUT_QUERY_KEY } from "./useGetLastPayout";
import {
  getGetPayoutSettingsQueryKey,
  getPayoutSettingsResponseSchema,
  getPayoutSettingsUrl,
  mapPayoutSettingsToResponseWithDestination,
  type UseGetPayoutSettingsResponse,
} from "./useGetPayoutSettings";

export interface UseUpdatePayoutSettingsOptions {
  includeExternalAccount?: boolean;
}

export function useUpdatePayoutSettings(options: UseUpdatePayoutSettingsOptions = {}) {
  const { includeExternalAccount = false } = options;
  const worker = useDefinedWorker();
  const queryClient = useCbhQueryClient();

  return useMutation({
    mutationFn: async (data: PayoutSettingsAttributes) => {
      return await patch({
        url: getPayoutSettingsUrl(worker.userId),
        data: {
          data: {
            id: worker.userId,
            type: payoutSettingsEntityType,
            attributes: data,
          },
        },
        queryParams: includeExternalAccount ? { include: externalAccountsEntityType } : undefined,
        responseSchema: getPayoutSettingsResponseSchema,
      });
    },
    onSuccess: (response) => {
      queryClient.setQueryData<UseGetPayoutSettingsResponse>(
        getGetPayoutSettingsQueryKey(worker.userId, false),
        mapPayoutSettingsToResponseWithDestination(response.data, false)
      );

      if (includeExternalAccount) {
        queryClient.setQueryData<UseGetPayoutSettingsResponse>(
          getGetPayoutSettingsQueryKey(worker.userId, true),
          mapPayoutSettingsToResponseWithDestination(response.data, true)
        );
      }

      // make sure we re-fetch the last payout
      // if it's failed, BFF will have added a "retry-payout" action to it, since we updated payout settings!
      void queryClient.invalidateQueries({
        queryKey: GET_LAST_PAYOUT_QUERY_KEY,
        refetchType: "all",
      });
    },
    onError: (error) => {
      const message =
        isAxiosError(error) &&
        isDefined(error.response) &&
        typeof error.response.data.message === "string"
          ? (error.response.data.message as string)
          : "Could not update payout settings!";

      enqueueSnackbar({
        variant: "error",
        message,
      });

      logEvent(APP_V2_APP_EVENTS.PAYOUT_SETTINGS_UPDATE_SETTINGS_FAILURE, { error });
    },
  });
}
